import type { GuidedView } from "./routing/guided-views.js";
import { GuidedDock } from "./chrome/GuidedDock.js";
import { GuidedPageHeader } from "./chrome/GuidedPageHeader.js";
import { GuidedToast } from "./chrome/GuidedToast.js";
import { GuidedHomeView } from "./views/GuidedHomeView.js";
import { GuidedProjectsView } from "./views/GuidedProjectsView.js";
import { GuidedNewProjectView } from "./views/GuidedNewProjectView.js";
import { GuidedProjectRoomView } from "./views/GuidedProjectRoomView.js";
import { GuidedMoreAdvancedView } from "./views/GuidedMoreAdvancedView.js";
import { GuidedScanView } from "./views/GuidedScanView.js";
import { GuidedOpportunitiesView } from "./views/GuidedOpportunitiesView.js";
import type { CommandCenterStatCard, GuidedNextActionCard, SimpleProjectPhaseState } from "./adapters/guided-project-steps.js";
import type { ProofChainStep } from "../../derive-studio-actions.js";
import type { StudioApiClient } from "../../api.js";
import type { GuidedStartMethod } from "../studio-shared/types/guided-new-project.js";
import type { StudioDepartureKitStatusV1, StudioIntakeSummaryV1 } from "@enrein/shared-contracts";
import type { SitePackageId } from "@enrein/shared-contracts";
import type { PackageEligibilityBlocked } from "../studio-shared/workflow/studio-workflow.js";
import { GuidedLoadingShell } from "./chrome/GuidedToast.js";

export interface GuidedShellProps {
  readonly view: GuidedView;
  readonly projectId: string | null;
  readonly startMethod: GuidedStartMethod | null;
  readonly toast: string | null;
  readonly workflowBusy: boolean;
  readonly validationIssues: readonly string[];
  readonly businessName: string | null;
  readonly phaseState: SimpleProjectPhaseState | null;
  readonly intakeSummary: StudioIntakeSummaryV1 | null;
  readonly intakeStatusLabel: string;
  readonly projectRows: readonly { readonly projectId: string; readonly name: string; readonly status: string }[];
  readonly projectsLoading: boolean;
  readonly projectsReady: boolean;
  readonly projectsError: string | null;
  readonly projectLoading: boolean;
  readonly projectLoadError: string | null;
  readonly previewUrl: string | null;
  readonly previewOpenAllowed: boolean;
  readonly departureKit: StudioDepartureKitStatusV1 | null;
  readonly handoffError: string | null;
  readonly nextActions: readonly GuidedNextActionCard[];
  readonly commandCenterStats: readonly CommandCenterStatCard[];
  readonly commandCenterStatsLoading: boolean;
  readonly commandCenterStatsError: string | null;
  readonly confirmationCount: number;
  readonly proofChainSteps: readonly ProofChainStep[];
  readonly api: StudioApiClient;
  readonly selectedOpportunityClaimId: string | null;
  readonly onSelectedOpportunityClaimIdChange: (claimId: string | null) => void;
  readonly onScanComplete: (publicReportRef: string) => void;
  readonly onConfirmationQueueChanged: () => void;
  readonly onNavigate: (view: GuidedView, projectId?: string, startMethod?: GuidedStartMethod | null) => void;
  readonly onNewProject: (input: {
    businessName: string;
    contact: string;
    projectType: string;
    startMethod: GuidedStartMethod;
    demoPreset?: import("../studio-shared/types/guided-demo-presets.js").GuidedStudioDemoPreset | null;
  }) => void;
  readonly onPrimaryAction: (actionId: string) => void;
  readonly onOpenPortal: () => void;
  readonly onOpenPreview: () => void;
  readonly onHomeAction: (actionId: string, projectId: string) => void;
  readonly packageEligibilityBlocked?: PackageEligibilityBlocked | null;
  readonly onBuildWithPackage?: (packageId: SitePackageId) => void;
  readonly onEnrichProfile?: () => void;
}

function renderView(props: GuidedShellProps) {
  const {
    view,
    projectId,
    startMethod,
    workflowBusy,
    validationIssues,
    businessName,
    phaseState,
    intakeSummary,
    intakeStatusLabel,
    projectRows,
    projectsLoading,
    projectsReady,
    projectsError,
    projectLoading,
    projectLoadError,
    previewUrl,
    previewOpenAllowed,
    departureKit,
    handoffError,
    nextActions,
    commandCenterStats,
    commandCenterStatsLoading,
    commandCenterStatsError,
    confirmationCount,
    proofChainSteps,
    api,
    selectedOpportunityClaimId,
    onSelectedOpportunityClaimIdChange,
    onScanComplete,
    onConfirmationQueueChanged,
    onNavigate,
    onNewProject,
    onPrimaryAction,
    onOpenPortal,
    onOpenPreview,
    onHomeAction,
    packageEligibilityBlocked,
    onBuildWithPackage,
    onEnrichProfile,
  } = props;

  switch (view) {
    case "home":
      return (
        <GuidedHomeView
          commandCenterStats={commandCenterStats}
          statsLoading={commandCenterStatsLoading}
          statsError={commandCenterStatsError}
          nextActions={nextActions}
          onNavigate={(v, pid) => onNavigate(v as GuidedView, pid)}
          onAction={onHomeAction}
        />
      );
    case "projects":
      return (
        <GuidedProjectsView
          rows={projectRows}
          loading={projectsLoading}
          ready={projectsReady}
          error={projectsError}
          onOpenProject={(pid) => onNavigate("project", pid)}
        />
      );
    case "new":
      return (
        <GuidedNewProjectView
          initialStartMethod={startMethod}
          busy={workflowBusy}
          validationIssues={validationIssues}
          onSubmit={onNewProject}
        />
      );
    case "scan":
      return (
        <GuidedScanView
          api={api}
          onScanComplete={onScanComplete}
          onNavigate={(v) => onNavigate(v as GuidedView)}
        />
      );
    case "scan-opportunities":
      return (
        <GuidedOpportunitiesView
          api={api}
          selectedClaimId={selectedOpportunityClaimId}
          onSelectedClaimIdChange={onSelectedOpportunityClaimIdChange}
          onNavigate={(v) => onNavigate(v as GuidedView)}
        />
      );
    case "project":
      if (projectLoading) {
        return (
          <section className="enrein-guided-view is-active" id="view-project">
            <p className="enrein-guided-muted">Loading projectâ€¦</p>
          </section>
        );
      }
      if (projectLoadError) {
        return (
          <section className="enrein-guided-view is-active" id="view-project">
            <p className="enrein-guided-validation">{projectLoadError}</p>
            <button type="button" className="enrein-guided-btn enrein-guided-btn--line" onClick={() => onNavigate("projects")}>
              View projects
            </button>
          </section>
        );
      }
      if (!projectId || !phaseState || !businessName) {
        return (
          <section className="enrein-guided-view is-active" id="view-project">
            <p className="enrein-guided-muted">Select a project from Home or Projects to open the project room.</p>
            <button type="button" className="enrein-guided-btn enrein-guided-btn--line" onClick={() => onNavigate("projects")}>
              View projects
            </button>
          </section>
        );
      }
      return (
        <GuidedProjectRoomView
          businessName={businessName}
          phaseState={phaseState}
          intakeSummary={intakeSummary}
          intakeStatusLabel={intakeStatusLabel}
          previewUrl={previewUrl}
          previewOpenAllowed={previewOpenAllowed}
          departureKit={departureKit}
          handoffError={handoffError}
          busy={workflowBusy}
          validationIssues={validationIssues}
          packageEligibilityBlocked={packageEligibilityBlocked}
          onBuildWithPackage={onBuildWithPackage}
          onEnrichProfile={onEnrichProfile}
          onPrimaryAction={onPrimaryAction}
          onOpenPortal={onOpenPortal}
          onOpenPreview={onOpenPreview}
          onNavigate={(v) => onNavigate(v as GuidedView, projectId)}
        />
      );
    case "more":
      return (
        <GuidedMoreAdvancedView
          api={api}
          confirmationCount={confirmationCount}
          proofChainSteps={proofChainSteps}
          onConfirmationQueueChanged={onConfirmationQueueChanged}
        />
      );
    default:
      return null;
  }
}

export function GuidedShell(props: GuidedShellProps) {
  return (
    <div className="enrein-guided-app">
      <GuidedToast message={props.toast} />
      <div className="enrein-guided-shell">
        <GuidedDock activeView={props.view} onNavigate={(v) => props.onNavigate(v)} />
        <main className="enrein-guided-main" id="enrein-guided-main">
          <GuidedPageHeader view={props.view} />
          <div className="enrein-guided-canvas">{renderView(props)}</div>
        </main>
      </div>
    </div>
  );
}

export { GuidedLoadingShell };
